import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const DAY_MS = 86400000;
const ISO_DATE = /\d{4}-\d{2}-\d{2}/g;

/**
 * @typedef {object} DeliveryEstimateRefs
 * @property {HTMLElement} rangeStart
 * @property {HTMLElement} rangeEnd
 * @property {HTMLElement} [orderedDate]
 * @property {HTMLElement} [processingDate]
 * @property {HTMLElement} [deliveryDate]
 * @property {HTMLButtonElement} [infoButton]
 * @property {HTMLElement} [infoNote]
 */

/**
 * Renders the "Receive your package between …" estimate.
 *
 * Dates are calculated in the browser (not in Liquid) so they stay correct on cached
 * pages. All calendar maths uses UTC Date objects that represent a calendar day in the
 * store's time zone, which keeps it free of DST and offset bugs.
 *
 * @extends {Component<DeliveryEstimateRefs>}
 */
class DeliveryEstimate extends Component {
  requiredRefs = ['rangeStart', 'rangeEnd'];

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /** Shows or hides the "estimates are approximate" note under the heading. */
  toggleNote() {
    const { infoButton, infoNote } = this.refs;
    if (!infoButton || !infoNote) return;

    const open = infoButton.getAttribute('aria-expanded') !== 'true';
    infoButton.setAttribute('aria-expanded', String(open));
    infoNote.hidden = !open;
  }

  // A variant change can morph the product details, which resets the date text.
  #onVariantUpdate = () => requestAnimationFrame(() => this.#render());

  #render() {
    const { dataset } = this;

    const processingDays = parseWeekdays(dataset.processingDays, [1, 2, 3, 4, 5]);
    const deliveryDays = dataset.deliveryBusinessDays === 'true' ? new Set([1, 2, 3, 4, 5]) : new Set([0, 1, 2, 3, 4, 5, 6]);
    const holidays = new Set(dataset.holidays?.match(ISO_DATE) ?? []);
    const [processingMin, processingMax] = parseRange(dataset.processingMin, dataset.processingMax);
    const [deliveryMin, deliveryMax] = parseRange(dataset.deliveryMin, dataset.deliveryMax);
    const cutoff = Number(dataset.cutoff) || 24 * 60;

    /** @param {Date} date */
    const isProcessingDay = (date) => processingDays.has(date.getUTCDay()) && !holidays.has(toIso(date));
    /** @param {Date} date */
    const isDeliveryDay = (date) =>
      deliveryDays.has(date.getUTCDay()) && (deliveryDays.size === 7 || !holidays.has(toIso(date)));

    const { today, minutes } = storeNow(dataset.timezone);

    // Orders after the cutoff, or on a day off, start processing on the next working day.
    const start = isProcessingDay(today) && minutes < cutoff ? today : nextDay(today, isProcessingDay);

    const shipEarliest = addDays(start, processingMin, isProcessingDay);
    const shipLatest = addDays(start, processingMax, isProcessingDay);
    const arriveEarliest = addDays(shipEarliest, deliveryMin, isDeliveryDay);
    const arriveLatest = addDays(shipLatest, deliveryMax, isDeliveryDay);

    const format = dataset.dateFormat === 'day_month' ? 'day_month' : 'month_day';
    const { rangeStart, rangeEnd, orderedDate, processingDate, deliveryDate } = this.refs;

    rangeStart.textContent = formatDate(arriveEarliest, format);
    rangeEnd.textContent = formatDate(arriveLatest, format);
    if (orderedDate) orderedDate.textContent = formatDate(today, format);
    if (processingDate) processingDate.textContent = formatRange(shipEarliest, shipLatest, format);
    if (deliveryDate) deliveryDate.textContent = formatRange(arriveEarliest, arriveLatest, format);

    this.classList.add('is-ready');
  }
}

/**
 * The current calendar day and minute of the day in the given IANA time zone.
 * Falls back to the visitor's own clock when the zone is blank or invalid.
 * @param {string | undefined} timeZone
 */
function storeNow(timeZone) {
  /** @type {Intl.DateTimeFormatOptions} */
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  };

  let parts;
  try {
    parts = new Intl.DateTimeFormat('en-US', { ...options, timeZone: timeZone || undefined }).formatToParts(new Date());
  } catch {
    parts = new Intl.DateTimeFormat('en-US', options).formatToParts(new Date());
  }

  /** @param {string} type */
  const get = (type) => Number(parts.find((part) => part.type === type)?.value ?? 0);

  return {
    today: new Date(Date.UTC(get('year'), get('month') - 1, get('day'))),
    minutes: (get('hour') % 24) * 60 + get('minute'),
  };
}

/**
 * @param {string | undefined} value - comma separated weekday numbers, 0 = Sunday
 * @param {number[]} fallback
 */
function parseWeekdays(value, fallback) {
  const days = (value ?? '')
    .split(',')
    .filter((day) => day !== '')
    .map(Number)
    .filter((day) => day >= 0 && day <= 6);

  return new Set(days.length ? days : fallback);
}

/**
 * @param {string | undefined} min
 * @param {string | undefined} max
 */
function parseRange(min, max) {
  const a = Math.max(0, Number(min) || 0);
  const b = Math.max(0, Number(max) || 0);
  return a <= b ? [a, b] : [b, a];
}

/**
 * The first matching day after `date`.
 * @param {Date} date
 * @param {(date: Date) => boolean} isWorkingDay
 */
function nextDay(date, isWorkingDay) {
  let next = new Date(date.getTime() + DAY_MS);
  // Guard against a schedule with no working days at all.
  for (let i = 0; i < 366 && !isWorkingDay(next); i++) next = new Date(next.getTime() + DAY_MS);
  return next;
}

/**
 * @param {Date} date
 * @param {number} count
 * @param {(date: Date) => boolean} isWorkingDay
 */
function addDays(date, count, isWorkingDay) {
  let result = date;
  for (let i = 0; i < count; i++) result = nextDay(result, isWorkingDay);
  return result;
}

/** @param {Date} date */
function toIso(date) {
  return date.toISOString().slice(0, 10);
}

/** @param {Date} date */
function monthName(date) {
  return date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
}

/**
 * @param {Date} date
 * @param {'month_day' | 'day_month'} format
 */
function formatDate(date, format) {
  const day = date.getUTCDate();
  return format === 'day_month' ? `${day} ${monthName(date)}` : `${monthName(date)} ${day}`;
}

/**
 * "Oct 22 – 27", "Oct 30 – Nov 3", or a single date when both ends match.
 * @param {Date} from
 * @param {Date} to
 * @param {'month_day' | 'day_month'} format
 */
function formatRange(from, to, format) {
  if (toIso(from) === toIso(to)) return formatDate(from, format);

  if (from.getUTCMonth() === to.getUTCMonth()) {
    return format === 'day_month'
      ? `${from.getUTCDate()} – ${to.getUTCDate()} ${monthName(to)}`
      : `${monthName(from)} ${from.getUTCDate()} – ${to.getUTCDate()}`;
  }

  return `${formatDate(from, format)} – ${formatDate(to, format)}`;
}

if (!customElements.get('dpack-delivery-estimate')) {
  customElements.define('dpack-delivery-estimate', DeliveryEstimate);
}
